const PIN_STORAGE_KEY = "tpv_access_pin";
const MIN_PIN_LENGTH = 4;
const MAX_PIN_LENGTH = 6;
const PBKDF2_ITERATIONS = 120000;

function bytesToBase64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary);
}

function base64ToBytes(value) {
  const binary = window.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

async function derivePin(pin, salt, iterations = PBKDF2_ITERATIONS) {
  const encoder = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    "raw",
    encoder.encode(pin),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await window.crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt,
      iterations,
      hash: "SHA-256",
    },
    key,
    256,
  );
  return new Uint8Array(bits);
}

function constantTimeEqual(left, right) {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left[index] ^ right[index];
  }
  return difference === 0;
}

export function isValidPinFormat(pin) {
  return (
    /^\d+$/.test(pin) &&
    pin.length >= MIN_PIN_LENGTH &&
    pin.length <= MAX_PIN_LENGTH
  );
}

export function getPinConfig() {
  try {
    const stored = JSON.parse(localStorage.getItem(PIN_STORAGE_KEY) || "null");
    if (!stored?.salt || !stored?.hash) {
      return { configured: false, length: MIN_PIN_LENGTH };
    }
    return {
      configured: true,
      length: Math.min(
        MAX_PIN_LENGTH,
        Math.max(MIN_PIN_LENGTH, Number(stored.length) || MIN_PIN_LENGTH),
      ),
    };
  } catch {
    return { configured: false, length: MIN_PIN_LENGTH };
  }
}

export function hasPinConfigured() {
  return getPinConfig().configured;
}

export async function savePin(pin) {
  if (!isValidPinFormat(pin)) {
    throw new Error("El PIN debe tener entre 4 y 6 dígitos");
  }

  if (!window.crypto?.subtle) {
    throw new Error("Este dispositivo no permite configurar el PIN de forma segura");
  }

  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const hash = await derivePin(pin, salt);

  localStorage.setItem(
    PIN_STORAGE_KEY,
    JSON.stringify({
      version: 1,
      length: pin.length,
      iterations: PBKDF2_ITERATIONS,
      salt: bytesToBase64(salt),
      hash: bytesToBase64(hash),
    }),
  );
}

export async function verifyPin(pin) {
  const config = getPinConfig();
  if (!config.configured || !isValidPinFormat(pin)) return false;

  try {
    const stored = JSON.parse(localStorage.getItem(PIN_STORAGE_KEY));
    const salt = base64ToBytes(stored.salt);
    const expectedHash = base64ToBytes(stored.hash);
    const actualHash = await derivePin(pin, salt, stored.iterations || PBKDF2_ITERATIONS);
    return constantTimeEqual(actualHash, expectedHash);
  } catch {
    return false;
  }
}

export function clearPin() {
  localStorage.removeItem(PIN_STORAGE_KEY);
}

export { MAX_PIN_LENGTH, MIN_PIN_LENGTH };
